import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "../../lib/api";
import ImageUploader from "./ImageUploader";

const emptyForm = {
    id: null,
    image: "",
    caption: "",
    order: 0,
};

export default function SpaceManager() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/space");
            setItems(data);
        } catch (e) {
            toast.error(formatError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const openAdd = () => {
        setForm(emptyForm);
        setModalOpen(true);
    };

    const openEdit = (item) => {
        setForm({
            id: item.id,
            image: item.image,
            caption: item.caption || "",
            order: item.order || 0,
        });
        setModalOpen(true);
    };

    const onSave = async (e) => {
        e.preventDefault();
        if (!form.image) {
            toast.error("Añade una imagen.");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                image: form.image,
                caption: form.caption || "",
                order: Number(form.order) || 0,
            };
            if (form.id) {
                await api.put(`/space/${form.id}`, payload);
                toast.success("Foto actualizada");
            } else {
                await api.post("/space", payload);
                toast.success("Foto añadida");
            }
            setModalOpen(false);
            setForm(emptyForm);
            await load();
        } catch (err) {
            toast.error(formatError(err));
        } finally {
            setSaving(false);
        }
    };

    const onDelete = async (item) => {
        if (!window.confirm("¿Eliminar esta foto del espacio?")) return;
        try {
            await api.delete(`/space/${item.id}`);
            toast.success("Foto eliminada");
            setItems((prev) => prev.filter((i) => i.id !== item.id));
        } catch (err) {
            toast.error(formatError(err));
        }
    };

    return (
        <section data-testid="space-manager">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
                <div>
                    <h2 className="font-display text-3xl text-[#1C1A18]">
                        Gestión del Espacio
                    </h2>
                    <p className="text-sm text-[#6b665f] font-light mt-2">
                        Sube las fotos del local / estudio que se muestran en la web.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={openAdd}
                    data-testid="space-add-btn"
                    className="inline-flex items-center gap-2 px-5 py-3 btn-gold text-[10px] uppercase self-start"
                >
                    <Plus size={14} /> Nueva foto
                </button>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-20">
                    <Loader2 className="animate-spin text-[#C4A882]" size={32} />
                </div>
            ) : items.length === 0 ? (
                <div className="text-center py-20 text-[#6b665f] font-light text-sm">
                    Todavía no hay fotos del espacio. Pulsa "Nueva foto" para añadir la primera.
                </div>
            ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {items.map((item) => (
                        <article
                            key={item.id}
                            data-testid={`space-admin-item-${item.id}`}
                            className="group relative bg-white border border-[#f0ece4] overflow-hidden"
                        >
                            <div className="aspect-[4/5] bg-[#f0ece4]">
                                <img
                                    src={item.image}
                                    alt={item.caption || "Espacio"}
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <div className="p-3 flex items-center justify-between gap-2">
                                <div className="min-w-0">
                                    <p className="text-[10px] tracking-[0.32em] uppercase text-[#C4A882] truncate">
                                        {item.caption || "Sin descripción"}
                                    </p>
                                    <p className="text-[10px] text-[#6b665f] mt-0.5 uppercase tracking-widest">
                                        #{item.order}
                                    </p>
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                    <button
                                        type="button"
                                        onClick={() => openEdit(item)}
                                        className="p-2 text-[#6b665f] hover:text-[#1C1A18]"
                                        aria-label="Editar"
                                        data-testid={`space-edit-${item.id}`}
                                    >
                                        <Pencil size={14} />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => onDelete(item)}
                                        className="p-2 text-[#6b665f] hover:text-red-600"
                                        aria-label="Eliminar"
                                        data-testid={`space-delete-${item.id}`}
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            </div>
                        </article>
                    ))}
                </div>
            )}

            {modalOpen && (
                <div
                    role="dialog"
                    aria-modal="true"
                    data-testid="space-modal"
                    className="fixed inset-0 z-[100] bg-[#1C1A18]/80 flex items-center justify-center p-4 overflow-y-auto"
                    onClick={() => !saving && setModalOpen(false)}
                >
                    <form
                        onSubmit={onSave}
                        onClick={(e) => e.stopPropagation()}
                        className="bg-[#FAFAF8] w-full max-w-2xl my-12 p-8 lg:p-10 grid grid-cols-1 md:grid-cols-2 gap-8"
                    >
                        <div>
                            <h3 className="font-display text-2xl text-[#1C1A18] mb-4">
                                {form.id ? "Editar foto" : "Nueva foto del espacio"}
                            </h3>
                            <ImageUploader
                                value={form.image}
                                onChange={(v) => setForm((f) => ({ ...f, image: v }))}
                                testIdPrefix="space-image"
                            />
                        </div>

                        <div className="space-y-5">
                            <div>
                                <label className="block text-[10px] tracking-[0.4em] uppercase text-[#6b665f] mb-2">
                                    Descripción (opcional)
                                </label>
                                <input
                                    type="text"
                                    value={form.caption}
                                    onChange={(e) =>
                                        setForm((f) => ({ ...f, caption: e.target.value }))
                                    }
                                    placeholder="Ej. Sala principal con luz natural"
                                    maxLength={160}
                                    className="field"
                                    data-testid="space-caption-input"
                                />
                            </div>

                            <div>
                                <label className="block text-[10px] tracking-[0.4em] uppercase text-[#6b665f] mb-2">
                                    Orden (menor = primero)
                                </label>
                                <input
                                    type="number"
                                    value={form.order}
                                    onChange={(e) =>
                                        setForm((f) => ({ ...f, order: e.target.value }))
                                    }
                                    className="field"
                                    data-testid="space-order-input"
                                />
                            </div>

                            <div className="flex flex-col sm:flex-row gap-3 pt-4">
                                <button
                                    type="submit"
                                    disabled={saving}
                                    data-testid="space-save-btn"
                                    className="flex-1 px-6 py-3 btn-gold text-[10px] uppercase disabled:opacity-60"
                                >
                                    {saving ? "Guardando…" : form.id ? "Guardar cambios" : "Añadir foto"}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setModalOpen(false)}
                                    disabled={saving}
                                    data-testid="space-cancel-btn"
                                    className="px-6 py-3 btn-outline-ink text-[10px] uppercase"
                                >
                                    Cancelar
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            )}
        </section>
    );
}
